import { Component, Inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { CheckboxModule } from 'primeng/checkbox';

import { FirebaseAuthService } from '../services/firebase-auth.service';
import { AUTH_CONFIG, AuthConfig } from '../models/auth-token-settings';

@Component({
  selector: 'dc-signup',
  templateUrl: './dc-signup.component.html',
  styleUrls: ['./dc-signup.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule, ButtonModule, InputGroupModule, InputTextModule, MessageModule, CheckboxModule],
})
export class DCSignupComponent {
  signupForm: FormGroup;
  loading = signal(false);
  error = signal<string | null>(null);

  constructor(
    @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
    private fb: FormBuilder,
    private fbAuthService: FirebaseAuthService,
    private router: Router,
  ) {
    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      terms: [false, Validators.requiredTrue],
    });
  }

  public async signupWithEmail() {
    if (this.signupForm.invalid) return;

    this.loading.set(true);
    this.error.set(null);
    const { email, password } = this.signupForm.value;
    try {
      await this.fbAuthService.register(email, password);
    } catch (err: any) {
      console.error('Signup error', err);
      this.error.set(err?.message || 'An unknown error occurred.');
    } finally {
      this.loading.set(false);
    }
  }

  public goToLogin() {
    const uri = this.authConfig?.settings?.loginRedirectUri || '/auth/login';
    this.router.navigate([uri]);
  }

  public async selectGoogleLogin() {
    await this.handleSocialLogin(() => this.fbAuthService.loginWithGoogle(), 'Google');
  }

  public async selectAppleLogin() {
    await this.handleSocialLogin(() => this.fbAuthService.signInWithApple(), 'Apple');
  }

  private async handleSocialLogin(loginAction: () => Promise<any>, provider: string) {
    if (!this.signupForm.controls['terms'].value) {
      this.error.set('You must accept the terms and conditions.');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    try {
      await loginAction();
    } catch (error: any) {
      this.error.set(error.message);
      console.error(`${provider} login error:`, error);
    } finally {
      this.loading.set(false);
    }
  }
}
